import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { JSX, ReactNode, RefObject } from 'react'

import { CollaborationNotesProvider } from '~publish/components/CollaborationNotes'
import { NotesFeed } from '~publish/components/Notes'
import {
  CreatePostNoteForm,
  FocusAfterDelete,
  PostNotesFeed,
} from '~publish/components/PostNotes'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { contentItemNoteSubject } from '~publish/hooks/notes/noteSubject'

import { ClosedThreadNotice } from './ClosedThreadNotice'
import { contentItemNotesAdapter } from './contentItemNotesAdapter'
import type { ContentItemNotesThread } from './useContentItemNotesThread'

/**
 * The open Notes thread of a draft, for any host: the composer's side panel
 * and the draft details dialog. The host reads the thread state with
 * `useContentItemNotesThread` and passes it in, so it also decides whether to
 * offer Notes at all.
 *
 * A null or failed summary shows as unavailable, never as an empty thread
 * with a composer: unlike a Post, nothing else on these surfaces can confirm
 * the account may write here. Once the draft is scheduled, the composer gives
 * way to `ClosedThreadNotice`.
 */
export function DraftNotesThread({
  contentItemId,
  thread,
  markClosed,
  header,
  renderBody = (children): ReactNode => children,
  statusClassName,
  fallbackFocusRef,
  focusComposer = false,
}: {
  contentItemId: string
  thread: ContentItemNotesThread
  markClosed: () => void
  header: ReactNode
  /** Wraps the feed and composer, as `SidePanel.Body` does in the composer. */
  renderBody?: (children: ReactNode) => ReactNode
  statusClassName?: string
  /** Takes focus after the last Note is deleted when there is no composer. */
  fallbackFocusRef: RefObject<HTMLElement | null>
  /** Focuses the composer once the thread is ready, as a popover opens. */
  focusComposer?: boolean
}): JSX.Element {
  const createFieldRef = useRef<HTMLDivElement>(null)
  const [focusAfterDeleteRequested, setFocusAfterDeleteRequested] =
    useState(false)
  const organizationId = useOrganizationId()
  const subject = useMemo(
    () => contentItemNoteSubject(contentItemId),
    [contentItemId],
  )
  const mentionScope = useMemo(
    () => (organizationId ? { organizationId } : null),
    [organizationId],
  )
  const ready = thread.status === 'ready'
  useLayoutEffect(() => {
    if (focusComposer && ready) createFieldRef.current?.focus()
  }, [focusComposer, ready])
  const requestFocusAfterDelete = useCallback(
    (): void => setFocusAfterDeleteRequested(true),
    [],
  )

  if (thread.status !== 'ready') {
    return (
      <>
        {header}
        {renderBody(
          <NotesFeed.Empty className={statusClassName}>
            <NotesFeed.Status>
              {thread.status === 'loading'
                ? 'Loading notes…'
                : 'Notes are unavailable.'}
            </NotesFeed.Status>
          </NotesFeed.Empty>,
        )}
      </>
    )
  }

  return (
    <CollaborationNotesProvider
      subject={subject}
      adapter={contentItemNotesAdapter}
      canCreate={thread.canCreate}
      isClosed={thread.isClosed}
      onThreadClosed={markClosed}
      focusAfterDelete={requestFocusAfterDelete}
      mentionScope={mentionScope}
    >
      {header}
      {renderBody(
        <>
          <PostNotesFeed label="Draft notes" />
          {thread.isClosed ? (
            <ClosedThreadNotice />
          ) : (
            thread.canCreate && <CreatePostNoteForm fieldRef={createFieldRef} />
          )}
        </>,
      )}
      <FocusAfterDelete
        requested={focusAfterDeleteRequested}
        onHandled={(): void => setFocusAfterDeleteRequested(false)}
        focusCreate={(): void =>
          (createFieldRef.current ?? fallbackFocusRef.current)?.focus()
        }
      />
    </CollaborationNotesProvider>
  )
}
